import { useCallback, useEffect, useMemo, useState } from "react";
import { type NavigateOptions, Outlet, useHref, useLocation, useNavigate } from "react-router";
import { MessageChatSquare } from "@untitledui/icons";
import { AppProviders, DashboardShell, ThemeToggle, type NavItemDividerType, type NavItemType } from "@mfa-aff/ui";
import { useLogout } from "../api/auth";
import { leaveToAdmin, useStopImpersonation } from "../api/impersonation";
import { useMe } from "../api/me";
import { ChannelCard } from "../components/ChannelCard";
import { FEEDBACK_HASH, FeedbackDialog } from "../components/FeedbackDialog";
import { ImpersonationBar } from "../components/ImpersonationBar";
import { NotificationBell } from "../components/NotificationBell";
import { QuoteOfTheMoment } from "../components/QuoteOfTheMoment";
import { RulesNotice } from "../components/RulesNotice";
import { ambassadorNavItem, partnerNav } from "../nav";
import { PartnerFooter } from "../components/PartnerFooter";

export const PartnerLayout = () => {
    const navigate = useNavigate();
    const { pathname, search, hash } = useLocation();
    const { data: me } = useMe();
    const logout = useLogout();
    const stopViewing = useStopImpersonation();
    const [feedbackOpen, setFeedbackOpen] = useState(false);

    // «پیشنهاد / گزارش مشکل» sits under the main items as a link to `#feedback` on the current page,
    // since the shell's nav takes links only; the link opens the dialog instead of navigating.
    const items = useMemo<(NavItemType | NavItemDividerType)[]>(
        () => [
            ...partnerNav,
            ...(me?.features.ambassador ? [ambassadorNavItem] : []),
            { divider: true },
            { label: "پیشنهاد / گزارش مشکل", href: `${pathname}${search}${FEEDBACK_HASH}`, icon: MessageChatSquare },
        ],
        [me?.features.ambassador, pathname, search],
    );

    // Nav links go through React Aria's router: catch `#feedback` there so no history entry is added.
    const routerNavigate = useCallback(
        (path: string, options?: NavigateOptions) => {
            if (path.endsWith(FEEDBACK_HASH)) setFeedbackOpen(true);
            else navigate(path, options);
        },
        [navigate],
    );

    // Any other way in (a notification, a typed or shared URL): open the dialog and drop the hash.
    useEffect(() => {
        if (hash !== FEEDBACK_HASH) return;
        setFeedbackOpen(true);
        navigate({ pathname, search }, { replace: true });
    }, [hash, pathname, search, navigate]);

    // Never render the shell (and its data) before the session is confirmed:
    // no flash of the dashboard for signed-out visitors; the API client redirects on 401.
    if (!me) {
        return (
            <div className="flex min-h-dvh items-center justify-center bg-primary" role="status" aria-label="در حال بررسی ورود">
                <span className="size-8 animate-spin rounded-full border-2 border-brand-solid border-t-transparent" />
            </div>
        );
    }

    const view = me.impersonation;
    // An admin's read-only view: «خروج» ends the view instead of signing anyone out.
    const signOut = view
        ? () => stopViewing.mutate(undefined, { onSettled: (res) => leaveToAdmin(res?.adminUrl, view.adminUrl) })
        : () => logout.mutate(undefined, { onSettled: () => navigate("/login", { replace: true }) });

    return (
        <AppProviders navigate={routerNavigate} useHref={useHref}>
            {view && <ImpersonationBar view={view} partnerName={me.affiliate.displayName} status={me.affiliate.status} />}
            <DashboardShell
                activeUrl={pathname}
                items={items}
                caption="بازاریابی"
                account={
                    me
                        ? { id: String(me.affiliate.id), name: me.affiliate.displayName, email: me.affiliate.email ?? me.affiliate.mobile ?? me.affiliate.refCode, status: "online" }
                        : undefined
                }
                accountMenuItems={[
                    { label: "حساب و پروفایل", href: "/account" },
                    { label: "اعلان‌ها", href: "/notifications" },
                    { label: "چطور کار می‌کند", href: "/how-it-works" },
                    { label: "واژه‌نامه", href: "/glossary" },
                    { label: "قوانین", href: "/rules" },
                ]}
                onSignOut={signOut}
                navCard={me.features.channelUrl ? <ChannelCard url={me.features.channelUrl} /> : undefined}
                headerStart={<QuoteOfTheMoment />}
                headerActions={
                    <>
                        <ThemeToggle />
                        <NotificationBell />
                    </>
                }
            >
                <RulesNotice rules={me.rules} />
                <Outlet />
                <PartnerFooter />
            </DashboardShell>
            <FeedbackDialog isOpen={feedbackOpen} onOpenChange={setFeedbackOpen} />
        </AppProviders>
    );
};

/** Sign-in and sign-up pages: no sidebar, same providers. */
export const PartnerAuthLayout = () => {
    const navigate = useNavigate();

    return (
        <AppProviders navigate={navigate} useHref={useHref}>
            <Outlet />
        </AppProviders>
    );
};
